import { IOAuthUser, ISessionUserDetails } from "@/api_framework/api_modals/FirebaseLogin";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { RESET_AUTH_STATE, setOAuthUser, setUserValid } from "@/app_redux/reducers/auth_reducer";
import { RESET_CUSTOMER_PROFILE } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import envConfig from "envConfig";
import { FirebaseError, initializeApp } from "firebase/app";
import { GoogleAuthProvider, getAuth, signInWithPopup, signOut } from "firebase/auth";
import { useState } from "react";
import toast from "react-hot-toast";
import { redirect } from "react-router";
import { ROUTES } from "@app/routes";
import { useConfiguration } from "../../useConfiguration";
import { useAuthMiddlewares } from "./useAuthMiddlewares";
import { auth } from "@/firebase";

export const useFirebaseLogin = () => {
  const { validateAccessToken } = useAuthMiddlewares();
  const [accessToken, setAccessToken] = useState<string>(null);
  const configuration = useConfiguration();
  const {
    env: { version: appVersion },
  } = useConfiguration();
  const dispatch = useAppDispatch();

  // ✅ Reuse shared auth
  const provider = new GoogleAuthProvider();

  const clientSignOut = () => {
    try {
      signOut(auth);

      localStorage.removeItem(configuration.localStorage.ACCESS_TOKEN.key);
      localStorage.removeItem(configuration.localStorage.CLINIC.key);
      localStorage.removeItem(configuration.localStorage.CITY.key);
      localStorage.removeItem(configuration.localStorage.CLINIC_NAME.key);
      localStorage.removeItem(configuration.localStorage.USER_DETAILS.key);
      localStorage.removeItem(configuration.localStorage.PWL_FILTERS.key);

      sessionStorage.clear();
      document.cookie.split(";").forEach((cookie) => {
        const name = cookie.split("=")[0].trim();
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      });

      dispatch(RESET_CUSTOMER_PROFILE());
      dispatch(RESET_AUTH_STATE());

      toast.success("Successfully Logged Out");
    } catch {
      toast.error("Error while logging out");
    }
  };

  const validateAccessTokenAndSignOut = (accessToken: string) => {
    if (!validateAccessToken(accessToken)) {
      clientSignOut();
    }
  };

  const signInWithGoogle = async () => {
    localStorage.removeItem("VMS_LOGOUT_EVENT");

    try {
      const result = await signInWithPopup(auth, provider);

      const user: IOAuthUser = {
        displayName: result.user.displayName ?? "",
        email: result.user.email ?? "",
        photoURL: result.user.photoURL ?? "",
      };

      const sessionUserDetails: ISessionUserDetails = {
        displayName: user.displayName,
        email: user.email,
        photoURL: user.photoURL,
      };

      localStorage.setItem(configuration.localStorage.USER_DETAILS.key, JSON.stringify(sessionUserDetails));
      localStorage.setItem("user_email", user.email);
      localStorage.setItem(configuration.localStorage.APP_VERSION.key, appVersion);

      dispatch(setOAuthUser(user));
      dispatch(setUserValid({ isValid: false }));

      setAccessToken(result.user.accessToken);

      const expiry = Date.now() + 12 * 60 * 60 * 1000;
      localStorage.setItem("sessionExpiry", expiry.toString());

      return result.user.accessToken;
    } catch (error: any) {
      dispatch(setUserValid({ isValid: false }));
      toast.error(`Error with login ${error.message}`);
      redirect(ROUTES.LOGIN_PAGE.url);
      setAccessToken(null);
      return null;
    }
  };

  return {
    signInWithGoogle,
    accessToken,
    clientSignOut,
    validateAccessTokenAndSignOut,
  };
};
export default useFirebaseLogin;
